import {
  Badge,
  Banner,
  Button,
  DataTable,
  FormField,
  FormGrid,
  IconButton,
  Page,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { DeliveryStatusT, Subscription } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FieldTemplate } from 'components/fieldTemplate';
import {
  getDeliveryStatus,
  getSubscription,
} from 'components/settings/api/webhooks';
import { useCopyWithDelay } from 'hooks/useCopyWithDelay';

import { mapDeliveryStatuses, webhooksBasePath } from '../../webhooks.utils';
import { DeliveryStatus } from '../deliveryStatus';

export function Events(): JSX.Element {
  const [subscription, setSubscription] = useState<Subscription>();
  const [deliveryStatus, setDeliveryStatus] =
    useState<Record<string, DeliveryStatusT>>();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const { isCopied: isUrlCopied, setIsCopied: setIsUrlCopied } =
    useCopyWithDelay(false);
  const { isCopied: isSecretCopied, setIsCopied: setIsSecretCopied } =
    useCopyWithDelay(false);

  const subscriptionEvents = subscription?.fqns || [];

  useEffect(() => {
    getAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAll = useCallback(() => {
    if (id) {
      setIsLoading(true);
      Promise.all([getSubscription(id), getDeliveryStatus(id)])
        .then((response) => {
          const [subscriptionResult, deliveryStatusResult] = response;
          const { data: subscription } = subscriptionResult;
          const {
            data: { delivery_statuses },
          } = deliveryStatusResult;

          setSubscription(subscription);
          setDeliveryStatus(mapDeliveryStatuses(delivery_statuses));
          setIsLoading(false);
        })
        .catch((e) => {
          setIsError(true);
          setIsLoading(false);
        });
    }
  }, [id]);

  const handleRoute = (to: string) => navigate(`${webhooksBasePath}/${to}`);

  const renderDetails = () => {
    return (
      <FormGrid>
        <FormField span={4}>
          <FieldTemplate
            readOnly
            noAlert
            label="Webhook url"
            input={{
              value: subscription?.url,
              onChange: () => null,
            }}
            suffix={{
              type: 'button',
              button: {
                icon: isUrlCopied ? 'done' : 'copy',
                onClick: () => {
                  if (subscription?.url) {
                    navigator.clipboard.writeText(subscription.url);
                    setIsUrlCopied(true);
                    ampli.settingsIntegrationsWebhooksViewCopyUrl();
                  }
                },
              },
            }}
            editorOptions={{ maxLines: 1 }}
          />
        </FormField>
        <FormField span={2}></FormField>

        <FormField span={4}>
          <FieldTemplate
            readOnly
            noAlert
            label="Secret"
            input={{
              value: subscription?.secret_signing_key,
              onChange: () => null,
            }}
            suffix={{
              type: 'button',
              button: {
                icon: isSecretCopied ? 'done' : 'copy',
                onClick: () => {
                  if (subscription?.secret_signing_key) {
                    navigator.clipboard.writeText(
                      subscription.secret_signing_key,
                    );
                    setIsSecretCopied(true);
                    ampli.settingsIntegrationsWebhooksViewCopySecret();
                  }
                },
              },
            }}
            editorOptions={{ maxLines: 1 }}
          />
        </FormField>
        <FormField span={2}></FormField>

        <FormField>
          {subscription?.is_enabled ? (
            <Banner
              variant="success"
              description={<>This webhook is enabled</>}
            />
          ) : (
            <Banner
              variant="info"
              description={<>This webhook is not enabled</>}
            />
          )}
        </FormField>
      </FormGrid>
    );
  };

  return (
    <>
      <Page.Section>{renderDetails()}</Page.Section>

      <Page.Section
        title={`${subscription?.name || ''} events`}
        titleMeta={
          <>
            {subscriptionEvents && subscriptionEvents?.length > 0 && (
              <Badge size="small" variant="secondary">
                {subscriptionEvents.length}
              </Badge>
            )}

            <Tooltip content="Help and documentation.">
              <IconButton
                icon="question"
                variant="tertiary"
                onClick={() =>
                  window.open(
                    'https://docs.dixa.io/docs/webhooks-guide',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              />
            </Tooltip>
          </>
        }
        controls={
          <Button
            data-testid="webhooks-event-edit-button"
            disabled={!id}
            onClick={() => {
              if (id) {
                ampli.settingsIntegrationsWebhooksViewEdit();
                handleRoute(`edit/${id}`);
              }
            }}
          >
            Edit webhook
          </Button>
        }
      >
        <DataTable<string, never>
          data={subscriptionEvents}
          tableStyleProps={{
            stickyHeader: true,
          }}
          tableStatusProps={{
            loading: isLoading,
            loadingFailed: isError,
            onLoadingRetry: () => getAll(),
            emptyState:
              subscriptionEvents.length === 0 && !isError
                ? {
                    title: 'Nothing to show here',
                    description: 'No events found',
                  }
                : undefined,
          }}
          columns={[
            {
              label: 'Event Id',
              selector: (event) => event,
            },
            {
              label: 'Status',
              selector: (event) => {
                const status =
                  (deliveryStatus && deliveryStatus[event]) || null;

                return (
                  status && (
                    <DeliveryStatus
                      key="status"
                      code={status?.response_code}
                      text={status?.response_text}
                      isSuccess={status?.is_success}
                    />
                  )
                );
              },
            },
            {
              label: 'Timestamp',
              selector: (event) => {
                const status =
                  (deliveryStatus && deliveryStatus[event]) || null;

                return status?.delivery_timestamp ? (
                  <Timestamp key="timestamp" time={status.delivery_timestamp} />
                ) : null;
              },
            },
            {
              label: undefined,
              style: {
                width: 0,
              },
              selector: (event) => (
                <Tooltip content="View event">
                  <IconButton
                    data-testid="webhooks-event-item-button"
                    variant="tertiary"
                    icon="eye"
                    onClick={() => handleRoute(`${id}/${event}`)}
                  />
                </Tooltip>
              ),
            },
          ]}
        />
      </Page.Section>
    </>
  );
}
